import { COURSE_TYPE, ICourse, STATUS } from "@types";

import { SITE_URL } from "./constants";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
  IInstructorDetailInput,
  InstructorDetail,
} from "@features/instructor/types";
import { formatDurationToISO } from "./formatDurationToISO";
import { getCourseCoverImage } from "./course";
import {
  IHowToLearnDetailInput,
  IHowToLearnDetailProductV2,
} from "@features/how-to-learn/types";
dayjs.extend(duration);

export function SearchAction() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://faradars.org/",
    url: "https://faradars.org/",
    name: "Faradars",
    potentialAction: {
      "@type": "SearchAction",
      name: "Faradars Website",
      target: "https://faradars.org/search?&q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };
}

const organizationConfig = {
  image: "https://faradars.org/next/logo-1920x1080.png",
  url: "https://faradars.org",
  sameAs: [
    "https://facebook.com/Faradars",
    "https://twitter.com/faradars",
    "https://instagram.com/faradars",
    "https://www.linkedin.com/company/faradars/",
    "https://youtube.com/c/FaraDarsEdu",
  ],
  logo: {
    "@type": "ImageObject",
    "@id": "https://faradars.org/#/schema/logo/image",
    inLanguage: "fa-IR",
    url: "https://faradars.org/next/logo-1920x1080.png",
    contentUrl: "https://faradars.org/next/logo-1920x1080.png",
    width: 1920,
    height: 1080,
    caption: "فرادرس",
  },
  name: "FaraDars",
  email: "info@faradars.org",
  telephone: "+98-21-57916000",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Motahari",
    addressLocality: "Tehran",
    addressCountry: "IR",
    addressRegion: "iran",
    postalCode: "1587616813",
  },
};
export function organization() {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    openingHours: "Sa,Su,Mo,Tu,We  7:30-16:30",
    ...organizationConfig,
  };
}
export function organizationOnline() {
  return {
    "@context": "https://schema.org",
    "@type": "OnlineBusiness",
    ...organizationConfig,
  };
}

interface ImageObjectProps {
  url: string;
  caption: string;
  width: string;
  height: string;
  contentUrl: string;
  uploadDate?: string;
  isPartOf?: string;
}

export function imageObject({
  url,
  caption,
  width,
  height,
  contentUrl,
  uploadDate,
  isPartOf,
}: ImageObjectProps) {
  return {
    "@context": "https://schema.org",
    "@id": url,
    "@type": "ImageObject",
    caption,
    representativeOfPage: "true",
    contentUrl,
    inLanguage: "fa-IR",
    url,
    width,
    height,
    ...(isPartOf ? { isPartOf: "" } : {}),
    ...(uploadDate ? { uploadDate } : {}),
    isFamilyFriendly: "true",
  };
}

interface ItemPageProps {
  url: string;
  name: string;
  datePublished?: string;
  imageUrl: string;
  mainContentSelector: string;
  aggregateRating?: {
    reviewCount?: number | undefined;
    "@type": "AggregateRating";
    ratingValue: number;
    ratingCount: number;
    bestRating: number;
    worstRating: number;
  };
  keywords?: string;
}

export const itemPage = ({
  url,
  name,
  datePublished,
  imageUrl,
  mainContentSelector,
  aggregateRating,
  keywords,
}: ItemPageProps) => {
  return {
    "@type": "ItemPage",
    "@id": url,
    url,
    name,
    ...(datePublished ? { datePublished } : {}),
    inLanguage: "fa-IR",
    primaryImageOfPage: {
      "@id": imageUrl,
    },
    mainContentOfPage: {
      "@context": "http://schema.org/",
      "@type": "WebPageElement",
      cssSelector: mainContentSelector,
    },
    ...(aggregateRating ? { aggregateRating } : {}),
    isFamilyFriendly: "true",
    ...(keywords ? { keywords } : {}),
  };
};

export function videoObjectList(course: ICourse) {
  const videoList = [];

  for (const [index, iterator] of course.lessons.entries()) {
    const durationInMinutes = Math.floor(Number(iterator.duration) / 60);

    const isVideosPage = course.product.attribute_type === COURSE_TYPE.VIDEO;
    const routeType = isVideosPage ? "/videos/" : "/courses/";

    const thumbnailImage = getCourseCoverImage(course);

    videoList.push({
      "@type":
        course.product.attribute_type === COURSE_TYPE.AUDIOBOOK ||
        course.product.attribute_type === COURSE_TYPE.PODCAST
          ? "AudioObject"
          : "VideoObject",
      position: index + 1,
      name: iterator.online_title || iterator.title,
      url: SITE_URL + routeType + course.product.slug,
      description:
        iterator.description || iterator.online_title || iterator.title,
      thumbnailUrl: [thumbnailImage],
      uploadDate: iterator.updated_at,
      duration: dayjs
        .duration(durationInMinutes > 0 ? durationInMinutes : 1, "m")
        .toISOString(),
      contentUrl: iterator.online_link,
      embedUrl: SITE_URL + routeType + course.product.slug,
      interactionStatistic: {
        "@type": "InteractionCounter",
        interactionType: { "@type": "WatchAction" },
        userInteractionCount: course.product.sold_count,
      },
    });
  }
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: videoList,
  };
}

export function singleVideoObject(
  course: ICourse,
  isVideos?: boolean,
  noAction?: boolean
) {
  const theLesson =
    course.lessons.find((lesson) => lesson.note === "openCourse") ||
    course.lessons[0];
  if (!theLesson) return;
  const parts = theLesson?.sections?.length
    ? theLesson.sections.map((section) => {
        const constKeys = {
          "@type": "Clip",
          name: section.title,
          startOffset: Number(section.start_seconds),
          endOffset: Number(section.end_seconds),
          url: `https://faradars.org/videos/${course.product.slug}?t=${section.start_seconds}`,
        };
        return section.thumbnail
          ? { ...constKeys, thumbnailUrl: section.thumbnail }
          : constKeys;
      })
    : [];

  const theDurationInSeconds =
    theLesson.duration ||
    String((course?.product?.duration?.in_minutes || 1) * 60);
  // const durationInMinutes = Math.floor(Number(theLesson.duration) / 60);

  const contentUrl = isVideos
    ? theLesson?.online_link_720 || theLesson?.online_link
    : theLesson.online_link;

  const thumbnailImage = getCourseCoverImage(course);

  const shouldNotHaveAction = noAction === true;

  if (isVideos) {
    if (shouldNotHaveAction) {
      return {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: course.product.title,
        duration: formatDurationToISO(theDurationInSeconds),
        uploadDate: course.product.published_at,
        thumbnailUrl: thumbnailImage,
        description:
          theLesson.description || theLesson.online_title || theLesson.title,
        contentUrl: contentUrl,
        hasPart: parts,
        interactionStatistic: {
          "@type": "InteractionCounter",
          interactionType: { "@type": "WatchAction" },
          userInteractionCount: course.product?.sold_count || 0,
        },
      };
    } else {
      return {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: course.product.title,
        duration: formatDurationToISO(theDurationInSeconds),
        uploadDate: course.product.published_at,
        thumbnailUrl: thumbnailImage,
        description:
          theLesson.description ||
          theLesson.online_title ||
          theLesson.title ||
          course.product.title,
        contentUrl: contentUrl,
        hasPart: parts,
        interactionStatistic: {
          "@type": "InteractionCounter",
          interactionType: { "@type": "WatchAction" },
          userInteractionCount: course.product?.sold_count || 0,
        },
        potentialAction: {
          "@type": "SeekToAction",
          target: `https://faradars.org/videos/${course.product.slug}?t={startOffset}`,
          "startOffset-input": "required name=startOffset",
        },
      };
    }
  }

  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: theLesson.online_title || theLesson.title || course.product.title,
    url: SITE_URL + "/videos/" + course.product.slug,
    description:
      theLesson.description || theLesson.online_title || theLesson.title,
    thumbnailUrl: thumbnailImage,
    duration: formatDurationToISO(theDurationInSeconds),
    contentUrl: contentUrl,
    embedUrl: SITE_URL + "/videos/" + course.product.slug,
    uploadDate: course.product.published_at,
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: { "@type": "WatchAction" },
      userInteractionCount: course.product.sold_count,
    },
    potentialAction: {
      "@type": "SeekToAction",
      target: `https://faradars.org/videos/${course.product.slug}?t={startOffset}`,
      "startOffset-input": "required name=startOffset",
    },
    hasPart: parts,
  };
}

export function courseObjectOld(course: ICourse) {
  const creator = [];
  const thing = [];
  if (course.product.instructors)
    for (const instructor of course.product.instructors || []) {
      creator.push({
        "@type": "Person",
        name: `${instructor.first_name} ${instructor.last_name}`,
        jobTitle: "",
        url: "",
      });
    }

  for (const category of course.product.categories || []) {
    thing.push({
      "@type": "Thing",
      url: `https://faradars.org/topic/${category.slug}`,
      name: category.name,
    });
  }

  const thumbnailImage = getCourseCoverImage(course);

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": course.product.id,
    provider: {
      "@type": "Organization",
      name: "FaraDars",
      url: "www.faradars.org",
    },
    publisher: {
      "@type": "Organization",
      name: "FaraDars",
      url: "www.faradars.org",
    },
    name: course.product.title,
    description: course.product.seo_metadesc,
    image: thumbnailImage,
    creator: creator,
    dateCreated: course.product.created_at,
    datePublished: course.product.published_at,
    thumbnailUrl: thumbnailImage,
    url: SITE_URL + "/courses/" + course.product.slug, ///course.product.url
    timeRequired: dayjs
      .duration(course.product.duration.in_minutes, "m")
      .toISOString(),
    about: thing,
  };
}

export function howToLearnItemListObject(
  _landing: IHowToLearnDetailInput,
  data: IHowToLearnDetailProductV2,
  url: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    url: url,
    numberOfItems: data?.products?.length || 0,
    itemListElement: (data?.products || [])?.map((product, index) => {
      const published = product.status === STATUS.PUBLISHED;
      return {
        "@type": "Course",
        provider: { "@type": "Organization", name: "FaraDars" },
        position: index + 1,
        image: product.png_image,
        url: product.url,
        name: product.title,
        description: product.title,
        isAccessibleForFree: product.price === 0,
        ...(product.instructors && {
          hasCourseInstance: [
            {
              "@type": "CourseInstance",
              courseMode: "Online",
              courseWorkload: dayjs
                .duration(product.duration.in_minutes, "m")
                .toISOString(),
              instructor: product.instructors.map((instructor) => ({
                "@type": "Person",
                name: instructor.first_name + " " + instructor.last_name,
                description: "",
                image: instructor.profile_picture,
              })),
            },
          ],
        }),
        offers: [
          {
            "@type": "Offer",
            category: "Paid",
            availability: published ? "OnlineOnly" : "PreOrder",
            priceCurrency: "IRR",
            price: product.price * 10,
          },
        ],
      };
    }),
  };
}

export function FaqListObject(landing: IHowToLearnDetailInput) {
  return {
    "@type": "FAQPage",
    mainEntity: landing?.data?.faqs?.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}
export function instructorItemListObject(data: InstructorDetail, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    url: url,
    numberOfItems: data.products.length || 0,
    itemListElement: data.products.map((product, index) => {
      const published = product.status === STATUS.PUBLISHED;
      return {
        "@type": "Course",
        provider: { "@type": "Organization", name: "FaraDars" },
        position: index + 1,
        image: product.png_image,
        url: product.url,
        name: product.title,
        description: product.title,
        isAccessibleForFree: product.price === 0,
        ...(product.instructors && {
          hasCourseInstance: [
            {
              "@type": "CourseInstance",
              courseMode: "Online",
              courseWorkload: dayjs
                .duration(product.duration.in_minutes, "m")
                .toISOString(),
              instructor: product.instructors.map((instructor) => ({
                "@type": "Person",
                name: instructor.first_name + " " + instructor.last_name,
                description: "",
                image: instructor.profile_picture,
              })),
            },
          ],
        }),
        offers: [
          {
            "@type": "Offer",
            category: "Paid",
            availability: published ? "OnlineOnly" : "PreOrder",
            priceCurrency: "IRR",
            price: product.price * 10,
          },
        ],
      };
    }),
  };
}

export function creativeWorkSeries(course: ICourse) {
  return {
    "@context": "https://schema.org/",
    "@type": "CreativeWorkSeries",
    name: course.product.title,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: course.product.rating.rate,
      bestRating: 5,
      worstRating: 1,
      ratingCount: course.product.rating.total,
    },
  };
}

export function breadCrumbObject(items: { id: string; name: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: "https://faradars.org/",
        name: "فرادرس",
      },
      ...items.map(({ id, name }, index) => ({
        "@type": "ListItem",
        position: index + 2,
        item: id,
        name,
      })),
    ],
  };
}

export function courseObject(course: ICourse) {
  const published = course.product.status === STATUS.PUBLISHED;
  const thumbnailImage = getCourseCoverImage(course);

  const audienceText = (course.product.description_data.good_for_strip ?? "")
    .replace(/[\r\n\t]+/g, "&")
    .split("&")
    .filter(Boolean);
  return {
    "@context": "https://schema.org/",
    "@type": "Course",
    name: course.product.title,
    description: course.product.seo_metadesc || course.product.title,
    publisher: {
      "@type": "Organization",
      name: "FaraDars",
      url: "www.faradars.org",
    },
    provider: {
      "@type": "Organization",
      name: "FaraDars",
      url: "www.faradars.org",
    },
    image: [thumbnailImage],
    ...(course.product.rating.total
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: course.product.rating.rate,
            ratingCount: course.product.rating.total,
            bestRating: 5,
            worstRating: 1,
            ...(course.product.commentCount
              ? { reviewCount: course.product.commentCount }
              : {}),
          },
        }
      : {}),
    offers: [
      {
        "@type": "Offer",
        category: "Paid",
        availability: published ? "OnlineOnly" : "PreOrder",
        ...(published && {
          priceCurrency: "IRR",
          price:
            (course.product.festival?.price_after_discount &&
              course.product.festival.price_after_discount * 10) ||
            course.product.price * 10,
        }),
      },
    ],
    ...(published && { totalHistoricalEnrollment: course.product.sold_count }),
    datePublished: course.product.published_at,
    about: course.product.tags?.map(({ name }) => name),
    inLanguage: "fa",
    availableLanguage: ["fa"],
    ...(published && {
      timeRequired: dayjs
        .duration(course.product.duration.in_minutes, "m")
        .toISOString(),
    }),
    coursePrerequisites: course.product.prerequisites.map(({ title }) => title),
    hasCourseInstance: [
      {
        "@type": "CourseInstance",
        courseMode: "Online",
        courseWorkload: dayjs
          .duration(published ? course.product.duration.in_minutes : 60, "m")
          .toISOString(),
        ...(course.product?.instructors && {
          instructor: course.product?.instructors.map((instructor) => ({
            "@type": "Person",
            name: instructor.first_name + " " + instructor.last_name,
            description: "",
            image: instructor.profile_picture,
          })),
        }),
      },
    ],
    interactionStatistic: [
      {
        "@type": "InteractionCounter",
        interactionType: "https://schema.org/LikeAction",
        userInteractionCount: course.product.favoriteCount,
      },
      ...(published
        ? [
            {
              "@type": "InteractionCounter",
              interactionType: "https://schema.org/CommentAction",
              userInteractionCount: course.product.commentCount,
            },
          ]
        : []),
    ],
    isAccessibleForFree: course.product.price === 0,
    ...(audienceText.length && {
      audience: {
        type: "@Audience",
        audienceType: audienceText,
      },
    }),
  };
}

export function personObject(instructor: InstructorDetail, slug: string) {
  const familyName = instructor.name
    .substring(instructor.name.indexOf(" ") + 1)
    .trim();

  return {
    "@context": "https://schema.org/",
    "@type": "Person",
    name: instructor.name,
    additionalName: instructor.name,
    affiliation: organization(),
    givenName: instructor.name.split(" ")[0],
    familyName: familyName,
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: {
        "@type": "ConsumeAction",
      },
      userInteractionCount: instructor.total_students,
    },
    nationality: {
      "@type": "Country",
      name: "Iran",
      sameAs: "https://en.wikipedia.org/wiki/Iran",
      alternateName: "Persia",
      identifier: "IR",
    },
    description: instructor.excerpt,
    image: instructor.photo,
    url: `https://faradars.org/instructors/${slug}`,
  };
}
